import {
  getBootstrapData,
  useBootstrapDataStore,
} from '@ui/bootstrap-data/bootstrap-data-store';

/**
 * Keeps every tab of one browser on the same account.
 *
 * The tabs share a single session cookie. When one of them logs out and
 * signs in as somebody else, the others keep the first account in their
 * header while every request they make already runs as the second: their
 * lists quietly change hands, the first account's own things answer 403,
 * and a folder or upload made from such a tab lands in the second account
 * without any error. So the signed-in user id is kept in localStorage,
 * written by whichever tab signs in or out, and a tab whose id no longer
 * matches reloads itself, the same outcome as pressing refresh there, before
 * anything can be clicked: a drive page comes back as the new account, or
 * lands on sign-in when nobody is signed in any more.
 *
 * The server backs this up. Every request carries the id the tab was
 * rendered for (X-Session-User) and the browser-session middleware answers
 * 409 session_user_changed when it differs from the session's user, or the
 * usual 401 session_ended when the session has no user any more; both make
 * the tab leave the stale page.
 */
export const SESSION_USER_HEADER = 'X-Session-User';

const STORAGE_KEY = 'ssp.session-user';

let started = false;
let leaving = false;

export function startCrossTabSessionSync(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  // this page was just rendered by the server, so its user is the truth
  writeSessionUser(currentUserId());

  // and so is every sign-in or sign-out this tab performs itself
  useBootstrapDataStore.subscribe((state, previous) => {
    const id = state.data?.user?.id ?? null;
    if (id !== (previous.data?.user?.id ?? null)) {
      writeSessionUser(id);
    }
  });

  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY || event.key === null) {
      checkStoredUser();
    }
  });
  // belt and braces for a storage event that was missed while hidden
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkStoredUser();
    }
  });
  window.addEventListener('focus', checkStoredUser);
}

/** True once this tab is reloading because its account changed. */
export function isLeavingForSessionChange(): boolean {
  return leaving;
}

/** The server said the account this tab was rendered for is gone. */
export function reloadForSessionChange(): void {
  if (leaving) return;
  leaving = true;
  window.location.reload();
}

/** The header that tells the server which account this tab was rendered for. */
export function sessionUserHeaders(): Record<string, string> {
  const id = currentUserId();
  return id == null ? {} : {[SESSION_USER_HEADER]: String(id)};
}

function currentUserId(): number | null {
  return getBootstrapData()?.user?.id ?? null;
}

function checkStoredUser(): void {
  if (leaving) return;
  const stored = readSessionUser();
  // nothing recorded yet, or storage is unavailable: the server check remains
  if (stored === null) return;
  const theirs = stored === '' ? null : Number(stored);
  if (theirs !== null && Number.isNaN(theirs)) return;
  if (theirs === currentUserId()) return;
  reloadForSessionChange();
}

function writeSessionUser(id: number | null): void {
  try {
    localStorage.setItem(STORAGE_KEY, id == null ? '' : String(id));
  } catch {
    // private mode or blocked storage: the server-side check still applies
  }
}

function readSessionUser(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
